import type { EffectLayer } from './effectLayer.types';
import type { AtmospherePreset } from './presets.config';
import { CAVE } from './presets/cave';
import { DUNGEON } from './presets/dungeon';
import { UNDERWATER } from './presets/underwater';
import { ARCTIC } from './presets/arctic';
import { VOLCANIC } from './presets/volcanic';
import { TEMPEST } from './presets/tempest';
import { ABYSS } from './presets/abyss';
import { FROSTSPIRE } from './presets/frostspire';
import { DESERT } from './presets/desert';
import { SKYLANDS } from './presets/skylands';
import { FOREST_JOURNEY_PRESETS } from './presets/forestJourney';
import { AUTUMN } from './presets/autumn';
import { SAKURA } from './presets/sakura';
import { ASHLANDS } from './presets/ashlands';
import { PETRIFIED } from './presets/petrified';
import { CONNECTED_DEPTH_BACKDROPS, CONNECTED_DEPTH_HEIGHTS } from '../../game/effects/atmosphere/silhouettes/connectedBiomeDepthBackdrops';
import { CONNECTED_SCENERY_BIOMES, isConnectedSceneryMap, type ConnectedSceneryMap } from '../../shared/scene/connectedSceneryMaps';
import { Z_LAYERS } from '../../game/rendering/Z_LAYERS';

const BASES: Record<ConnectedSceneryMap, AtmospherePreset> = {
  crystal_caverns: CAVE, ancient_temple: DUNGEON, sunken_ruins: UNDERWATER,
  frozen_lake: ARCTIC, volcano_summit: VOLCANIC,
  stormbreak_cliffs: TEMPEST, sunless_deep: ABYSS,
  frostspire_peaks: FROSTSPIRE, scorched_dunes: DESERT, skyreach_isles: SKYLANDS,
  dark_forest: FOREST_JOURNEY_PRESETS.forest_road,
  amber_grove: AUTUMN, sakura_vale: SAKURA, smoldering_wastes: ASHLANDS, stoneveil_barrens: PETRIFIED,
};
const FAR_ALPHA: Record<ConnectedSceneryMap, number> = {
  crystal_caverns: .52, ancient_temple: .52, sunken_ruins: .48,
  frozen_lake: .50, volcano_summit: .50,
  stormbreak_cliffs: .65, sunless_deep: .72,
  frostspire_peaks: .65, scorched_dunes: .64, skyreach_isles: .60, dark_forest: .60,
  amber_grove: .62, sakura_vale: .60, smoldering_wastes: .64, stoneveil_barrens: .60,
};

/** Build once from the unchanged generic presets. Non-far objects, ordering,
 * aliases, FX counts and original preset arrays remain untouched. */
const MAP_LAYERS = Object.fromEntries(Object.entries(BASES).map(([key, preset]) => {
  const id = key as ConnectedSceneryMap;
  return [id, preset.layers.map(layer => layer.kind === 'parallax' && layer.depth === Z_LAYERS.PARALLAX_FAR
    ? { ...layer, height: CONNECTED_DEPTH_HEIGHTS[id], alpha: FAR_ALPHA[id], svg: CONNECTED_DEPTH_BACKDROPS[id] }
    : layer)];
})) as Record<ConnectedSceneryMap, EffectLayer[]>;

/** Apply only after normal preset selection. Explicit editor layers return
 * before this selector; custom or unknown authored biomes retain their old
 * behavior even when normal selection falls back to the level-id preset. */
export function resolveConnectedMapDepth(
  levelId: string | undefined,
  authoredBiome: string | null | undefined,
  selected: EffectLayer[],
): EffectLayer[] {
  if (!isConnectedSceneryMap(levelId)) return selected;
  if (authoredBiome && authoredBiome !== CONNECTED_SCENERY_BIOMES[levelId]) return selected;
  return selected === BASES[levelId].layers ? MAP_LAYERS[levelId] : selected;
}
